// Proposal hub + editor
import { useEffect, useRef, useState } from "react";
import { api } from "../../shared/api/client";
import { Empty, Loading } from "../../shared/components/Empty";
import { Pill } from "../../shared/components/Pill";
import { toast } from "../../shared/stores/toasts";

type Section = { id: number; order: number; title: string; body_md: string };
type Proposal = {
  id: number; title: string; owner_id: number; status: string; template: string | null;
  section_count: number; created_at: string; updated_at: string;
};
type ProposalDetail = Proposal & { sections: Section[] };

const TEMPLATES = [
  { key: "SERVICE", label: "서비스 제안서", desc: "서비스 소개 및 견적 제안" },
  { key: "PARTNERSHIP", label: "파트너십 제안서", desc: "협력/파트너 관계 제안" },
  { key: "RFP", label: "RFP 응답서", desc: "입찰 제안 요청 대응" },
  { key: "GENERAL", label: "일반 제안서", desc: "범용 제안 템플릿" },
  { key: null, label: "빈 문서", desc: "섹션 없이 시작" },
];

const STATUS_KIND: Record<string, "ok" | "warn" | "muted" | "info"> = {
  DRAFT: "muted", REVIEW: "info", APPROVED: "ok", REJECTED: "warn", SUBMITTED: "info",
};

export default function ProposalPage() {
  const [view, setView] = useState<"hub" | "editor">("hub");
  const [editId, setEditId] = useState<number | null>(null);

  if (view === "editor" && editId !== null) {
    return <Editor proposalId={editId} onBack={() => { setView("hub"); setEditId(null); }} />;
  }
  return <Hub onOpen={(id) => { setEditId(id); setView("editor"); }} />;
}

function Hub({ onOpen }: { onOpen: (id: number) => void }) {
  const [proposals, setProposals] = useState<Proposal[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newTemplate, setNewTemplate] = useState<string | null>("SERVICE");

  const load = () =>
    api.get<Proposal[]>("/proposal")
      .then((r) => setProposals(r.data))
      .catch(() => setProposals([]));

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newTitle.trim()) return;
    try {
      const r = await api.post<ProposalDetail>("/proposal", {
        title: newTitle,
        template: newTemplate ?? undefined,
      });
      toast.success("제안서 생성됨");
      setCreating(false);
      setNewTitle("");
      onOpen(r.data.id);
    } catch (e: any) {
      toast.error("생성 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  const del = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm("제안서를 삭제할까요?")) return;
    try {
      await api.delete(`/proposal/${id}`);
      load();
    } catch {}
  };

  if (proposals === null) return <Loading />;

  return (
    <div className="t-grid cols-1">
      <div className="t-card" style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <span style={{ fontWeight: 700, fontSize: 16 }}>제안서 허브</span>
        <button className="t-btn solid" style={{ marginLeft: "auto" }} onClick={() => setCreating((v) => !v)}>
          {creating ? "취소" : "+ 새 제안서"}
        </button>
      </div>

      {creating && (
        <div className="t-card" style={{ display: "grid", gap: 10, maxWidth: 480 }}>
          <div>
            <label className="t-label">제목</label>
            <input className="t-input" value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
              placeholder="제안서 제목" onKeyDown={(e) => e.key === "Enter" && create()} />
          </div>
          <div>
            <label className="t-label">템플릿</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 6 }}>
              {TEMPLATES.map((t) => (
                <button key={String(t.key)} className="t-btn"
                  style={{
                    fontSize: 12, border: "1px solid",
                    borderColor: newTemplate === t.key ? "var(--p-500)" : "var(--border)",
                    color: newTemplate === t.key ? "var(--p-500)" : undefined,
                    background: newTemplate === t.key ? "var(--p-50, #eff6ff)" : "transparent",
                  }}
                  onClick={() => setNewTemplate(t.key)}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <button className="t-btn solid" onClick={create} disabled={!newTitle.trim()}>만들기</button>
        </div>
      )}

      {proposals.length === 0 && !creating && (
        <Empty msg="아직 제안서가 없습니다. 새 제안서를 만들어 보세요." />
      )}

      {proposals.length > 0 && (
        <div className="t-grid cols-2">
          {proposals.map((p) => (
            <div key={p.id} className="t-card" style={{ cursor: "pointer" }}
              onClick={() => onOpen(p.id)}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{p.title}</div>
                  {p.template && (
                    <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>{p.template}</div>
                  )}
                </div>
                <Pill kind={STATUS_KIND[p.status] ?? "muted"}>{p.status}</Pill>
                <button className="t-btn" style={{ fontSize: 11, padding: "2px 6px" }}
                  onClick={(e) => del(p.id, e)}>삭제</button>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                섹션 {p.section_count}개 · 수정 {new Date(p.updated_at).toLocaleDateString("ko-KR")}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="t-card">
        <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13 }}>템플릿으로 시작하기</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 8 }}>
          {TEMPLATES.filter((t) => t.key !== null).map((t) => (
            <button key={t.key!} className="t-btn"
              style={{ textAlign: "left", display: "flex", flexDirection: "column", gap: 4, padding: "10px 12px", border: "1px solid var(--border)", borderRadius: 8 }}
              onClick={() => { setNewTemplate(t.key); setCreating(true); }}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{t.label}</span>
              <span style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 400 }}>{t.desc}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Editor({ proposalId, onBack }: { proposalId: number; onBack: () => void }) {
  const [proposal, setProposal] = useState<ProposalDetail | null>(null);
  const [activeSecId, setActiveSecId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = () =>
    api.get<ProposalDetail>(`/proposal/${proposalId}`)
      .then((r) => {
        setProposal(r.data);
        if (r.data.sections.length > 0 && activeSecId === null) {
          setActiveSecId(r.data.sections[0].id);
        }
      })
      .catch(() => toast.error("제안서 로드 실패"));

  useEffect(() => { load(); }, [proposalId]);

  const activeSec = proposal?.sections.find((s) => s.id === activeSecId) ?? null;

  const saveSection = (id: number, body_md: string) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setSaving(true);
    saveTimer.current = setTimeout(async () => {
      try {
        await api.patch(`/proposal/${proposalId}/sections/${id}`, { body_md });
        setProposal((prev) => prev ? {
          ...prev,
          sections: prev.sections.map((s) => s.id === id ? { ...s, body_md } : s),
        } : prev);
      } finally {
        setSaving(false);
      }
    }, 800);
  };

  const updateStatus = async (status: string) => {
    try {
      const r = await api.patch<ProposalDetail>(`/proposal/${proposalId}`, { status });
      setProposal(r.data);
      toast.success("상태 변경됨");
    } catch {}
  };

  const snapshot = async () => {
    try {
      const r = await api.post<{ version: number }>(`/proposal/${proposalId}/versions`);
      toast.success("버전 저장됨", `v${r.data.version}`);
    } catch (e: any) {
      toast.error("버전 저장 실패", e?.response?.data?.error?.message ?? "");
    }
  };

  if (!proposal) return <Loading />;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* top bar */}
      <div className="t-card" style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 0, borderRadius: "8px 8px 0 0" }}>
        <button className="t-btn" onClick={onBack} style={{ fontSize: 12 }}>← 목록</button>
        <span style={{ fontWeight: 700, fontSize: 15, flex: 1 }}>{proposal.title}</span>
        {saving && <span style={{ fontSize: 11, color: "var(--text-3)" }}>저장 중…</span>}
        <Pill kind={STATUS_KIND[proposal.status] ?? "muted"}>{proposal.status}</Pill>
        <button className="t-btn" style={{ fontSize: 12 }} onClick={snapshot}>버전 저장</button>
      </div>

      <div style={{ display: "flex", flex: 1, gap: 0, minHeight: 0 }}>
        {/* left sidebar — section list */}
        <div style={{
          width: 200, borderRight: "1px solid var(--border)",
          padding: "12px 8px", display: "flex", flexDirection: "column", gap: 2,
          background: "var(--bg-1, #f9fafb)",
        }}>
          {proposal.sections.map((s) => (
            <button key={s.id}
              onClick={() => setActiveSecId(s.id)}
              style={{
                textAlign: "left", border: 0, borderRadius: 6, padding: "7px 10px", cursor: "pointer",
                background: activeSecId === s.id ? "var(--p-50, #eff6ff)" : "transparent",
                color: activeSecId === s.id ? "var(--p-600, #2563eb)" : "var(--text-1)",
                fontSize: 13, fontWeight: activeSecId === s.id ? 600 : 400,
              }}>
              <span style={{ color: "var(--text-3)", marginRight: 6, fontSize: 11 }}>{s.order + 1}.</span>
              {s.title}
            </button>
          ))}
        </div>

        {/* center editor */}
        <div style={{ flex: 1, padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
          {activeSec ? (
            <>
              <div style={{ fontWeight: 700, fontSize: 16, borderBottom: "1px solid var(--border)", paddingBottom: 8 }}>
                {activeSec.title}
              </div>
              <textarea
                style={{
                  flex: 1, border: "1px solid var(--border)", borderRadius: 8,
                  padding: 12, fontSize: 13, lineHeight: 1.7,
                  fontFamily: "inherit", resize: "none", minHeight: 400,
                  background: "var(--bg-0, white)",
                }}
                value={activeSec.body_md}
                placeholder={`${activeSec.title} 내용을 입력하세요 (Markdown 지원)`}
                onChange={(e) => {
                  const val = e.target.value;
                  setProposal((prev) => prev ? {
                    ...prev,
                    sections: prev.sections.map((s) => s.id === activeSec.id ? { ...s, body_md: val } : s),
                  } : prev);
                  saveSection(activeSec.id, val);
                }}
              />
            </>
          ) : (
            <Empty msg="좌측에서 섹션을 선택하세요" />
          )}
        </div>

        {/* right meta panel */}
        <div style={{
          width: 200, borderLeft: "1px solid var(--border)",
          padding: "16px 12px", display: "flex", flexDirection: "column", gap: 16,
          background: "var(--bg-1, #f9fafb)", fontSize: 13,
        }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>상태</div>
            <select className="t-input" style={{ height: 30, fontSize: 12 }}
              value={proposal.status}
              onChange={(e) => updateStatus(e.target.value)}>
              {["DRAFT", "REVIEW", "APPROVED", "REJECTED", "SUBMITTED"].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {proposal.template && (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>템플릿</div>
              <span style={{ fontSize: 12, color: "var(--text-3)" }}>{proposal.template}</span>
            </div>
          )}

          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>섹션</div>
            <span style={{ color: "var(--text-3)" }}>{proposal.section_count}개</span>
          </div>

          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>수정일</div>
            <span style={{ fontSize: 11, color: "var(--text-3)" }}>
              {new Date(proposal.updated_at).toLocaleDateString("ko-KR")}
            </span>
          </div>

          <div style={{ marginTop: "auto" }}>
            <button className="t-btn solid" style={{ width: "100%", fontSize: 12 }}
              onClick={() => updateStatus("SUBMITTED")}>
              결재 상신
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
